import { Action } from './global/types';

export const INITIAL_STATE = {
  loading: false,
  error: null,
  data: {},
  loaded: false,
};

export interface Type {
  type: string;
  mapper?: (state: any, action: any) => any;
}

interface ReducerParams {
  types: Type[];
  initialState: any;
}

export interface ActionGenerator {
  START: string;
  SUCCESS: string;
  FAIL: string;
  RESET: string;
}

export const shouldHandleType = (type: string, types: string[]): boolean =>
  types.includes(type);

export const buildTypeMappers = (
  types: Type[],
  initialState: any,
): Record<string, any> =>
  types.reduce(
    (acc, { type, mapper = () => initialState }) => ({
      ...acc,
      [type]: mapper,
    }),
    {},
  );

export const createReducer = ({
  types,
  initialState = INITIAL_STATE,
}: ReducerParams) => (state = initialState, action: Action) => {
  return !shouldHandleType(
    action.type,
    types.map(({ type }) => type),
  )
    ? state
    : buildTypeMappers(types, initialState)[action.type](state, action);
};

export const actionsGenerator = (type: string): ActionGenerator => ({
  START: `${type}_START`,
  SUCCESS: `${type}_SUCCESS`,
  FAIL: `${type}_FAIL`,
  RESET: `${type}_RESET`,
});

export const startMapper = (key: string) => (state: any) => ({
  ...state,
  [key]: {
    ...(state[key] || {}),
    loading: true,
  },
});

export const failMapper = (key: string) => (state: any, { error }: any) => ({
  ...state,
  [key]: {
    ...(state[key] || {}),
    loading: false,
    loaded: true,
    error,
  },
});

export const successMapper = (key: string) => (state: any, { data }: any) => ({
  ...state,
  [key]: {
    ...(state[key] || {}),
    loading: false,
    loaded: true,
    error: null,
    data,
  },
});

export const resetMapper = (key: string, initialState: any = INITIAL_STATE) => (
  state: any,
) => ({
  ...state,
  [key]: initialState,
});
